import { useState, useEffect } from 'react';
import {
  LogOut, ChevronRight, 
  Layers, Key, Activity, 
  User, Sparkles, Clock, Shield,
  CreditCard, Settings, DollarSign, ArrowUp, ExternalLink,
  Package, Zap, AlertCircle, CheckCircle, Plus, Loader2, X
} from 'lucide-react';
import { authService, subscriptionService } from '../../services/PlatformService';
import { formatNumber } from '../../lib/formatters';
import ApiKeysManager from './ApikeysManager';

const UserAccount = () => {
  const [activeTab, setActiveTab] = useState('usage');
  const [expandedSections, setExpandedSections] = useState({
    usageStats: true,
    costs: true
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    // Check for success parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_id')) {
      setSuccessMessage('Assinatura atualizada com sucesso! Suas mudanças serão refletidas em breve.');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (urlParams.get('package_success')) {
      setSuccessMessage('Pacote de tokens adicionado com sucesso!');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    loadData();
  }, [selectedPeriod]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [userResponse, subscriptionResponse] = await Promise.all([
        authService.getUser(),
        subscriptionService.getSubscriptionSummary(selectedPeriod)
      ]);
      setUserData(userResponse.data);
      setSubscriptionData(subscriptionResponse.data);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Falha ao carregar seus dados. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPackage = async () => {
    setIsProcessing(true);
    try {
      const response = await subscriptionService.purchaseTokenPackage();
      
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL de pagamento não recebida');
      }
    } catch (err) {
      console.error('Erro ao comprar pacote:', err);
      alert('Erro ao processar compra. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    window.location.href = '/login';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-3xl overflow-hidden bg-gradient-to-br from-green-100 to-green-100 p-1 animate-pulse">
            <div className="w-full h-full rounded-3xl overflow-hidden bg-white flex items-center justify-center">
              <User className="w-10 h-10 text-purple-400" />
            </div>
          </div>
          <p className="text-xl text-gray-700 font-medium">Carregando suas informações...</p>
        </div>
      </div>
    );
  }

  const user = userData || { name: "Usuário", email: "usuario@exemplo.com" };
  const subscription = subscriptionData?.subscription || {};
  const plan = subscription.plan || {};
  const tokens = subscriptionData.tokens || {};
  const resources = subscriptionData.resources || {};

  const sidebarTabs = [
    {
      id: 'usage',
      label: 'Consumo e Custos',
      icon: DollarSign,
      color: 'green'
    },
    {
      id: 'apikeys',
      label: 'Chaves de API',
      icon: Key,
      color: 'green'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50 animate-slide-in">
          <CheckCircle className="w-5 h-5" />
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="ml-2 hover:opacity-80"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Sidebar */}
          <aside className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden sticky top-8">
              {/* Header do Perfil */}
              <div className="p-6 bg-gradient-to-r from-green-600 via-green-600 to-green-600 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden">
                    {user.photo_url ? (
                      <img src={user.photo_url} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-10 h-10 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-xl truncate">{user.name}</h3>
                    <p className="text-sm text-white/90 truncate mt-1">{user.email}</p>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <nav className="p-3">
                <ul className="space-y-1">
                  {sidebarTabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <li key={tab.id}>
                        <button
                          onClick={() => !tab.soon && setActiveTab(tab.id)}
                          disabled={tab.soon}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl text-sm font-medium transition-all ${
                            activeTab === tab.id 
                              ? `bg-gradient-to-r from-${tab.color}-50 to-${tab.color}-100 text-${tab.color}-700 shadow-sm` 
                              : tab.soon
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5" />
                            <span>{tab.label}</span>
                          </div>
                          {tab.soon && (
                            <span className="text-xs bg-gray-200 text-gray-500 px-2 py-1 rounded-full">
                              Em breve
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>

                <div className="pt-4 mt-4 border-t border-gray-200">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-3 rounded-2xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all"
                  >
                    <LogOut className="w-5 h-5 mr-3" />
                    Sair da Conta
                  </button>
                </div>
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {activeTab === 'usage' && (
              <div className="space-y-6">
                {/* Limites de Recursos */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Shield className="w-6 h-6 text-green-600" />
                    Limites e Consumo
                  </h3>

                  <div className="space-y-6">
                                {/* Cards de Custos */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-blue-100 hover:shadow-2xl transition-all">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl">
                        <Layers className="w-6 h-6 text-blue-600" />
                      </div>
                      <Activity className="w-5 h-5 text-blue-400" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-600 mb-1">
                      Tokens de Entrada
                    </h3>
                    <p className="text-3xl font-bold text-gray-900">
                      {formatNumber(tokens.input?.used || 0)}
                    </p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-blue-100 hover:shadow-2xl transition-all">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl">
                        <Layers className="w-6 h-6 text-blue-600" />
                      </div>
                      <Activity className="w-5 h-5 text-blue-400" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-600 mb-1">
                      Tokens de Saída
                    </h3>
                    <p className="text-3xl font-bold text-gray-900">
                      {formatNumber(tokens.output?.used || 0)}
                    </p>
                  </div>
                </div>


                    {/* Outros Recursos */}
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-3">Recursos</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 border border-gray-200 rounded-2xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-600">Funcionários</span>
                            <span className="font-semibold">
                              {resources.agents?.used || 0}/{resources.agents?.limit || 3}
                            </span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-purple-500"
                              style={{ width: `${resources.agents?.percentage || 0}%` }}
                            />
                          </div>
                        </div>

                        <div className="p-4 border border-gray-200 rounded-2xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-600">Chamadas API</span>
                            <span className="font-semibold">
                              {resources.api_calls?.used || 0}/{(resources.api_calls?.used || 0) + (resources.api_calls?.available || 100)}
                            </span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500"
                              style={{ width: `${resources.api_calls?.percentage || 0}%` }}
                            />
                          </div>
                        </div>

                        <div className="p-4 border border-gray-200 rounded-2xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-600">Mensagens Plataforma</span>
                            <span className="font-semibold">
                              {resources.platform_messages?.used || 0}/{(resources.platform_messages?.used || 0) + (resources.platform_messages?.available || 100)}
                            </span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500"
                              style={{ width: `${resources.platform_messages?.percentage || 0}%` }}
                            />
                          </div>
                        </div>

                      </div>
                    </div>

                    {/* Alerta de Limite */}
                    {(tokens.input?.percentage > 80 || tokens.output?.percentage > 80) && (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-2xl flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-yellow-800">Aproximando-se do limite</h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            Você está usando mais de 80% dos seus tokens. Considere adicionar um pacote adicional.
                          </p>
                          <button
                            onClick={handleAddPackage}
                            className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded-xl hover:bg-yellow-700 transition-all text-sm font-medium"
                          >
                            Adicionar Pacote de Tokens
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'apikeys' && <ApiKeysManager />}
          </main>
        </div>
      </div>
    </div>
  );
};

export default UserAccount;